import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, CheckCircle } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        position: "",
        password: "",
        confirm_password: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.password !== form.confirm_password) {
            toast.error("Passwords do not match");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        const result = await register({
            full_name: form.full_name,
            email: form.email,
            phone: form.phone,
            position: form.position,
            password: form.password,
        });
        setSubmitting(false);
        if (result.success) {
            setSuccess(true);
        } else {
            toast.error(result.error || "Registration failed");
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
                <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-md p-8 text-center an-fade-in">
                    <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                        <CheckCircle size={32} weight="fill" className="text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#003366] mb-3" style={{ fontFamily: "Outfit" }}>
                        Registration submitted
                    </h2>
                    <p className="text-[#475569] leading-relaxed mb-6">
                        Your account is now <span className="font-semibold text-[#003366]">Pending Approval</span>.
                        An administrator will review your request shortly. You'll be able to sign in once approved.
                    </p>
                    <Button
                        onClick={() => navigate("/login")}
                        className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                        data-testid="register-success-back-to-login"
                    >
                        Back to sign in
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 sm:p-6">
            <div className="w-full max-w-xl bg-white border border-[#E2E8F0] rounded-md p-6 sm:p-10 an-fade-in">
                <div className="flex items-center gap-2.5 mb-7">
                    <div className="h-10 w-10 flex items-center justify-center">
                        <Logo size={40} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-bold text-base text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            ADVANCE NETWORKS
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            New technician registration
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Get started</div>
                    <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Create your technician account
                    </h2>
                    <p className="text-sm text-slate-600 mt-2">
                        New accounts require admin approval before first sign-in.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Full name *
                            </Label>
                            <Input
                                required
                                value={form.full_name}
                                onChange={handleChange("full_name")}
                                placeholder="John Smith"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Email *
                            </Label>
                            <Input
                                type="email"
                                required
                                value={form.email}
                                onChange={handleChange("email")}
                                placeholder="you@advance-net.co"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Phone
                            </Label>
                            <Input
                                value={form.phone}
                                onChange={handleChange("phone")}
                                placeholder="(555) 123-4567"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-phone-input"
                            />
                        </div>

                        <div className="space-y-2 sm:col-span-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Position / Role
                            </Label>
                            <Input
                                value={form.position}
                                onChange={handleChange("position")}
                                placeholder="Field Technician, Lead Installer, etc."
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-position-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Password *
                            </Label>
                            <Input
                                type="password"
                                required
                                value={form.password}
                                onChange={handleChange("password")}
                                placeholder="At least 8 characters"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-password-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Confirm password *
                            </Label>
                            <Input
                                type="password"
                                required
                                value={form.confirm_password}
                                onChange={handleChange("confirm_password")}
                                placeholder="Repeat password"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="register-confirm-password-input"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full h-11 bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium mt-2"
                        data-testid="register-submit-button"
                    >
                        {submitting ? "Creating account..." : (<><UserPlus size={18} className="mr-2" /> Create account</>)}
                    </Button>
                </form>

                <div className="mt-6 pt-5 border-t border-[#E2E8F0] text-sm text-slate-600">
                    Already have an account?{" "}
                    <Link
                        to="/login"
                        className="text-[#0066D9] font-semibold hover:underline"
                        data-testid="link-to-login"
                    >
                        Sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
